import { ClockIcon } from 'lucide-react';
import { ComponentProps, useState } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/tailwind-utils';
import { Temporal } from 'temporal-polyfill';
import { formatPlainTime } from '@airreading/common';
import { FormInputTimeColumns } from './components/form-input-time-columns';
import { FormInputTimeSelect } from './components/form-input-time-select';

export interface FormInputTimeProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label?: string;
  triggerButtonProps?: ComponentProps<typeof Button> & { 'data-testid'?: string };
  required?: boolean;
  step?: Temporal.Duration;
  min?: Temporal.PlainTime;
  max?: Temporal.PlainTime;
  className?: string;
  shouldDisplayError?: boolean;
  shouldDisplayIcon?: boolean;
  footerAction?: {
    label: string;
    onClick: (options: { closePopover: () => void }) => void;
  };
}

const DEFAULT_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;

export function FormInputTime<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label = '',
  triggerButtonProps = {},
  required = false,
  step = Temporal.Duration.from({ minutes: DEFAULT_MINUTES }),
  min = Temporal.PlainTime.from('00:00:00'),
  max = Temporal.PlainTime.from('23:59:00'),
  className = '',
  shouldDisplayError = true,
  shouldDisplayIcon = true,
  footerAction,
}: FormInputTimeProps<TFieldValues, TName>) {
  const { className: triggerButtonClassName, ...otherTriggerProps } = triggerButtonProps;
  const [isOpen, setIsOpen] = useState(false);

  const stepMinutes = step.total({ unit: 'minutes' });
  const validStepMinutes = stepMinutes < 1 || stepMinutes > MINUTES_PER_DAY ? DEFAULT_MINUTES : stepMinutes;
  const validStep = Temporal.Duration.from({ minutes: validStepMinutes });
  const FormInputTypeInnerComponent = validStepMinutes >= DEFAULT_MINUTES ? FormInputTimeSelect : FormInputTimeColumns;

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem className="flex flex-col">
            {label && (
              <FormLabel className="font-bold">
                {label}
                {required && '*'}
              </FormLabel>
            )}
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={'outline'}
                    className={cn(
                      'w-[125px] pl-4 text-left font-normal flex items-center justify-between',
                      !field.value && 'text-muted-foreground',
                      triggerButtonClassName,
                    )}
                    {...otherTriggerProps}
                  >
                    {field.value ? formatPlainTime(field.value, { hour12: true }) : <span>Pick a time</span>}
                    {shouldDisplayIcon && <ClockIcon className="h-4 w-4 opacity-50" />}
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="start">
                <FormInputTypeInnerComponent
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    // Close popover when a value is selected
                    if (validStepMinutes >= DEFAULT_MINUTES) {
                      setIsOpen(false);
                    }
                  }}
                  step={validStep}
                  min={min}
                  max={max}
                  className={className}
                  addMax={validStepMinutes >= DEFAULT_MINUTES}
                  footerAction={
                    footerAction
                      ? {
                          ...footerAction,
                          onClick: (opts) => {
                            footerAction.onClick({ ...opts, closePopover: () => setIsOpen(false) });
                          },
                        }
                      : undefined
                  }
                />
              </PopoverContent>
            </Popover>
            {shouldDisplayError && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}
